import type { SettingsAppStorage } from './settings';
import type { UserSettings } from './user';

export type CollectedDataItem = {
  name: string;
  content: string | string[];
  attributes?: Record<string, string | (string | undefined)[]>;
};

export const EventMessages = {
  REQUEST_CONFIG: 'request config',
  URL_CHANGED: 'url changed',
  IFRAME_LOADED: 'iframe loaded',
  DATA_CHANGED: 'data changed',
  SET_SETTINGS_STORAGE: 'set settings storage',
  TOGGLE_VISIBILITY: 'toggle visibility',
  TOGGLE_EMAIL_AGENT_VISIBILITY: 'toggle email agent visibility',
  DRAG_ICON: 'drag icon',
  REQUEST_BADGE_VISIBILITY_CONFIG: 'request badge visibility config',
  SET_DOMAIN: 'set domain',
  TRACK_EVENT: 'track event',
  LOGIN_AND_OPEN_EMAIL_AGENT: 'login and open email agent',
} as const;

export type EventDataChanged = { message: typeof EventMessages.DATA_CHANGED; url: string; data: CollectedDataItem[] };

export type EventData =
  | { message: typeof EventMessages.REQUEST_CONFIG }
  | { message: typeof EventMessages.URL_CHANGED | typeof EventMessages.IFRAME_LOADED; url: string; isOpen: boolean }
  | EventDataChanged
  | {
      message: typeof EventMessages.SET_SETTINGS_STORAGE;
      settingsStorage: SettingsAppStorage & UserSettings;
      url: string;
    }
  | {
      message: typeof EventMessages.TOGGLE_VISIBILITY;
      url: string;
      data: CollectedDataItem[];
      isOpen: boolean;
      fromBadge: boolean;
      fromToolbar: boolean;
      fromGoogleCalendar: boolean;
      fromGmail: boolean;
      fromOutlook: boolean;
    }
  | {
      message: typeof EventMessages.TOGGLE_EMAIL_AGENT_VISIBILITY;
      isOpen: boolean;
      instanceId: string;
    }
  | { message: typeof EventMessages.DRAG_ICON; url: string }
  | { message: typeof EventMessages.REQUEST_BADGE_VISIBILITY_CONFIG }
  | { message: typeof EventMessages.SET_DOMAIN; domain: string; url: string; widgetId?: string }
  | {
      message: typeof EventMessages.TRACK_EVENT;
      category: string;
      action: string;
      eventName: string;
      url: string;
      custom_data?: Record<string, unknown>;
    }
  | {
      message: typeof EventMessages.LOGIN_AND_OPEN_EMAIL_AGENT;
      instanceId: string;
    };

export const isEventData = (event: unknown): event is EventData => {
  return (
    typeof event === 'object' &&
    event !== null &&
    'message' in event &&
    Object.values(EventMessages).includes((event as EventData).message)
  );
};

export const isCollectedDataItem = (data: unknown): data is CollectedDataItem => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const item = data as CollectedDataItem;

  if (typeof item.name !== 'string') {
    return false;
  }

  if (typeof item.content !== 'string' && !Array.isArray(item.content)) {
    return false;
  }
  if (Array.isArray(item.content) && !item.content.every((element) => typeof element === 'string')) {
    return false;
  }

  if (item.attributes !== undefined) {
    if (typeof item.attributes !== 'object' || item.attributes === null) {
      return false;
    }
    for (const key in item.attributes) {
      const attrValue = item.attributes[key];
      if (typeof attrValue !== 'string' && !Array.isArray(attrValue)) {
        return false;
      }
      if (Array.isArray(attrValue) && !attrValue.every((el) => typeof el === 'string' || typeof el === 'undefined')) {
        return false;
      }
    }
  }

  return true;
};
